import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Minimize,
  Info,
} from "lucide-react";

const MinimumWindowSubstringVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Example 1",
      s: "ADOBECODEBANC",
      t: "ABC",
    },
    {
      id: 2,
      name: "Example 2",
      s: "a",
      t: "a",
    },
    {
      id: 3,
      name: "Example 3",
      s: "a",
      t: "aa",
    },
    {
      id: 4,
      name: "Duplicates in t",
      s: "BBAAC",
      t: "ABA",
    },
    {
      id: 5,
      name: "No Window",
      s: "cabwefgewcwaefgcf",
      t: "cae",
    },
    {
      id: 6,
      name: "Simple Case",
      s: "abacaba",
      t: "aab",
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customS, setCustomS] = useState("");
  const [customT, setCustomT] = useState("");
  const [s, setS] = useState(defaultTestCases[0].s);
  const [t, setT] = useState(defaultTestCases[0].t);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function minWindow(s, t):", indent: 0 },
    { line: "tMap = create frequency map of t", indent: 1 },
    { line: "left = 0, have = 0, need = tMap.size", indent: 1 },
    { line: "result = [-1, -1], minLength = infinity", indent: 1 },
    { line: "", indent: 0 },
    { line: "for right = 0 to s.length-1:", indent: 1 },
    { line: "char = s[right]", indent: 2 },
    { line: "windowMap[char]++", indent: 2 },
    { line: "if char in tMap and windowMap[char] == tMap[char]:", indent: 2 },
    { line: "have++", indent: 3 },
    { line: "", indent: 0 },
    { line: "while have == need:", indent: 2 },
    { line: "// Window is valid, try to shrink", indent: 3 },
    { line: "if (right - left + 1) < minLength:", indent: 3 },
    { line: "minLength = right - left + 1", indent: 4 },
    { line: "result = [left, right]", indent: 4 },
    { line: "", indent: 3 },
    { line: "leftChar = s[left]", indent: 3 },
    { line: "windowMap[leftChar]--", indent: 3 },
    {
      line: "if leftChar in tMap and windowMap[leftChar] < tMap[leftChar]:",
      indent: 3,
    },
    { line: "have--", indent: 4 },
    { line: "left++", indent: 3 },
    { line: "", indent: 0 },
    { line: "return s.substring(result[0], result[1] + 1)", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (s, t) => {
    const allSteps = [];
    if (!s || !t) return [];
    let stepCount = 0;

    const tMap = {};
    for (const char of t) {
      tMap[char] = (tMap[char] || 0) + 1;
    }
    const need = Object.keys(tMap).length;

    const addStep = (description, highlightLine, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description,
        highlightLine,
        tMap,
        need,
        ...details,
      });
    };

    let left = 0;
    let have = 0;
    let minLength = Infinity;
    let resultIndices = [-1, -1];
    let windowMap = {};

    addStep("Start: Create frequency map for t.", 1, {
      left,
      have,
      windowMap: {},
      resultIndices,
      minLength,
    });

    for (let right = 0; right < s.length; right++) {
      const char = s[right];
      addStep(`Expand: 'right' moves to ${right} ('${char}').`, 5, {
        left,
        have,
        windowMap: { ...windowMap },
        right,
        resultIndices,
        minLength,
      });

      windowMap[char] = (windowMap[char] || 0) + 1;
      addStep(`'${char}' count in window is now ${windowMap[char]}.`, 7, {
        left,
        have,
        windowMap: { ...windowMap },
        right,
        resultIndices,
        minLength,
      });

      if (tMap[char] && windowMap[char] === tMap[char]) {
        have++;
        addStep(`Met requirement for '${char}'. Have ${have}/${need}.`, 9, {
          left,
          have,
          windowMap: { ...windowMap },
          right,
          resultIndices,
          minLength,
        });
      }

      while (have === need) {
        addStep(
          "Window is valid (have == need). Check length and try to shrink.",
          11,
          {
            left,
            have,
            windowMap: { ...windowMap },
            right,
            resultIndices,
            minLength,
          }
        );

        const currentLength = right - left + 1;
        if (currentLength < minLength) {
          minLength = currentLength;
          resultIndices = [left, right];
          addStep(`New minimum window found! Length: ${minLength}.`, 14, {
            left,
            have,
            windowMap: { ...windowMap },
            right,
            resultIndices,
            minLength,
          });
        }

        const leftChar = s[left];
        addStep(`Shrink: 'left' moves from ${left} ('${leftChar}').`, 18, {
          left,
          have,
          windowMap: { ...windowMap },
          right,
          resultIndices,
          minLength,
        });
        windowMap[leftChar]--;

        if (tMap[leftChar] && windowMap[leftChar] < tMap[leftChar]) {
          have--;
          addStep(
            `Window is now invalid for '${leftChar}'. Have ${have}/${need}.`,
            20,
            {
              left,
              have,
              windowMap: { ...windowMap },
              right,
              resultIndices,
              minLength,
            }
          );
        }
        left++;
      }
    }

    addStep("End of string. Final result shown.", 23, {
      left,
      have,
      windowMap,
      right: s.length - 1,
      resultIndices,
      minLength,
      isFinal: true,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(s, t);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [s, t]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setS(testCases[index].s);
    setT(testCases[index].t);
  };

  const addCustomTestCase = () => {
    try {
      if (!customS || !customT)
        throw new Error("Strings 's' and 't' cannot be empty.");
      const newTestCase = {
        id: Date.now(),
        name: `Custom`,
        s: customS,
        t: customT,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomS("");
      setCustomT("");
    } catch (error) {
      alert(`Invalid input: ${error.message}`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];
  const finalResultString =
    currentStepData?.resultIndices?.[0] > -1
      ? s.substring(
          currentStepData.resultIndices[0],
          currentStepData.resultIndices[1] + 1
        )
      : "";

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Minimize className="text-blue-500" size={24} /> Minimum Window
        Substring
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given two strings `s` and `t`, return the minimum window
                substring of `s` such that every character in `t` (including
                duplicates) is included in the window. If there is no such
                substring, return an empty string.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      s = "{tc.s}", t = "{tc.t}"
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customS}
                onChange={(e) => setCustomS(e.target.value)}
                placeholder="String s: ADOBECODEBANC"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="text"
                value={customT}
                onChange={(e) => setCustomT(e.target.value)}
                placeholder="String t: ABC"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4 relative">
              <div className="flex flex-wrap gap-1">
                {(s || "").split("").map((char, idx) => (
                  <div key={idx} className="relative text-center">
                    <div
                      className={`w-8 h-8 flex items-center justify-center border-2 rounded font-mono text-lg font-bold transition-colors duration-300
                      ${
                        currentStepData?.left <= idx &&
                        idx <= currentStepData?.right
                          ? "bg-blue-100 border-blue-300"
                          : "bg-gray-100 border-gray-300"
                      }
                      ${
                        currentStepData?.resultIndices?.[0] <= idx &&
                        idx <= currentStepData?.resultIndices?.[1]
                          ? "bg-green-200 border-green-400"
                          : ""
                      }
                      ${
                        idx === currentStepData?.right
                          ? "border-blue-500 scale-110"
                          : ""
                      }
                      ${
                        idx === currentStepData?.left
                          ? "border-red-500 scale-110"
                          : ""
                      }
                    `}
                    >
                      {char}
                    </div>
                    <span className="text-xs text-gray-400 mt-1">{idx}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div
                className="col-span-2 p-2 rounded text-center"
                style={{
                  backgroundColor:
                    currentStepData?.have === currentStepData?.need
                      ? "#dcfce7"
                      : "#f1f5f9",
                }}
              >
                <h4 className="text-sm font-semibold text-gray-700">
                  Window Status
                </h4>
                <p className="font-mono text-2xl font-bold mt-1">
                  <span
                    className={
                      currentStepData?.have === currentStepData?.need
                        ? "text-green-600"
                        : "text-gray-600"
                    }
                  >
                    {currentStepData?.have || 0}
                  </span>{" "}
                  /{" "}
                  <span className="text-gray-800">
                    {currentStepData?.need || 0}
                  </span>
                </p>
                <p className="text-xs">Characters Matched / Needed</p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700">
                  Target (t) Map
                </h4>
                <div className="p-2 bg-gray-50 rounded mt-1 flex flex-wrap gap-2 justify-center text-xs font-mono">
                  {currentStepData &&
                    Object.entries(currentStepData.tMap).map(([key, val]) => (
                      <span key={key} className="p-1 bg-gray-200 rounded">
                        {key}:{val}
                      </span>
                    ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700">
                  Window Map
                </h4>
                <div className="p-2 bg-gray-50 rounded mt-1 flex flex-wrap gap-2 justify-center text-xs font-mono">
                  {currentStepData &&
                    Object.entries(currentStepData.windowMap)
                      .filter(([k, v]) => v > 0)
                      .map(([key, val]) => (
                        <span key={key} className="p-1 bg-gray-200 rounded">
                          {key}:{val}
                        </span>
                      ))}
                </div>
              </div>

              <div className="col-span-2 text-center">
                <h4 className="text-sm font-semibold text-gray-700">
                  Best Window Found
                </h4>
                <p className="p-2 bg-green-100 text-green-800 font-mono text-lg rounded mt-1">
                  "{finalResultString}"
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.s h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Sliding Window Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              This problem is a classic example of the sliding window technique.
              We use two pointers (`left` and `right`) to define a window. The
              `right` pointer expands the window while we track characters
              needed from `t` in a hash map. Once the window is "valid"
              (contains all characters from `t`), we shrink it from the `left`
              to find the smallest possible valid window, updating our result
              whenever we find a new minimum.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N + M)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(N + M)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N = length of s, M = length of t.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Two hash maps are used: one for the target string `t` and one
                  for the current window in `s`.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  A `have` and `need` counter system efficiently tracks when the
                  window becomes valid, avoiding recounting all characters on
                  every move.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The core logic is a loop of expansion (moving `right`) and
                  contraction (moving `left` when the window is valid).
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-blue-100 border-2 border-blue-500">
                  abc
                </div>
                <span>Current Window</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-green-200 border-2 border-green-400">
                  abc
                </div>
                <span>Best Window Found</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded border-2 border-blue-500"></div>
                <span>Right Pointer</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded border-2 border-red-500"></div>
                <span>Left Pointer</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MinimumWindowSubstringVisualizer;
